/**
 * Kinematics Scoring Utilities
 * 
 * Computes scores for kinematic metrics based on benchmark bands.
 * 
 * Scoring Rules:
 * - If value is WITHIN ideal range [min, max]: score = 100
 * - If value is OUTSIDE ideal range: score decreases as distance from nearest boundary increases
 * - Labels (HIGH/MEDIUM/LOW) are determined by score bands, not raw magnitude:
 *   - HIGH: score >= 80
 *   - MEDIUM: score >= 50 and < 80
 *   - LOW: score < 50
 */

import { 
  KinematicParamKey, 
  ParamRange, 
  DEFAULT_METRIC_WEIGHTS,
  METRIC_DEFS
} from '../config/kinematicsBenchmarks';

export type RangeStatus = "BELOW_RANGE" | "IN_RANGE" | "ABOVE_RANGE";
export type ScoreLabel = "HIGH" | "MEDIUM" | "LOW" | "PROXY" | "PLACEHOLDER";

export interface MetricScore {
  paramKey: KinematicParamKey;
  value: number;
  benchmark: ParamRange;
  score: number; // 0-100
  inRange: boolean; // Deprecated: use rangeStatus instead
  rangeStatus: RangeStatus;
  label: ScoreLabel;
  reason?: string; // For invalid/placeholder cases
}

export interface ScoringResult {
  overallScore: number; // 0-100 (weighted)
  metricsInRange: number;
  totalMetrics: number;
  metricScores: MetricScore[];
  topPriorities: MetricScore[]; // Lowest scoring metrics
  confidenceSummary: {
    high: number;
    medium: number;
    proxy: number;
    placeholder: number;
  };
}

/**
 * Score a single metric value against its benchmark range.
 * 
 * @param value - The metric value to score
 * @param benchmark - The ideal range {low, high}
 * @param tolerance - Optional tolerance for score decay (defaults to (max-min) * 0.5)
 * @returns Object with score (0-100), rangeStatus, and label
 */
export function scoreRange(
  value: number,
  benchmark: ParamRange,
  tolerance?: number
): { 
  score: number; 
  rangeStatus: RangeStatus; 
  label: ScoreLabel;
  reason?: string;
} {
  const { low, high } = benchmark;
  
  // Validate inputs
  if (
    value === null || 
    value === undefined || 
    !isFinite(value) ||
    low === null || 
    low === undefined || 
    !isFinite(low) ||
    high === null || 
    high === undefined || 
    !isFinite(high)
  ) {
    return {
      score: 0,
      rangeStatus: "IN_RANGE", // Default, but value is invalid
      label: "LOW",
      reason: "invalid"
    };
  }
  
  const rangeSize = high - low;
  
  // Check if value is within range (inclusive)
  if (value >= low && value <= high) {
    // Value is in range: score = 100
    return {
      score: 100,
      rangeStatus: "IN_RANGE",
      label: "HIGH" // Score 100 => HIGH label
    };
  }
  
  // Value is outside range: calculate distance and score
  let distance: number;
  let rangeStatus: RangeStatus;
  
  if (value < low) {
    distance = low - value;
    rangeStatus = "BELOW_RANGE";
  } else {
    distance = value - high;
    rangeStatus = "ABOVE_RANGE";
  }
  
  // Calculate tolerance (how quickly score decays)
  // If tolerance is explicitly provided and <= 0, use binary scoring
  if (tolerance !== undefined && tolerance <= 0) {
    return {
      score: 0, // Out of range = 0 score
      rangeStatus,
      label: "LOW"
    };
  }
  
  // Use provided tolerance or default
  const defaultTolerance = rangeSize > 0 ? rangeSize * 0.5 : Math.abs(low) + Math.abs(high);
  const effectiveTolerance = tolerance !== undefined && tolerance > 0 ? tolerance : defaultTolerance;
  
  // Safety check: if effectiveTolerance is still invalid, use binary scoring
  if (effectiveTolerance <= 0) {
    return {
      score: 0,
      rangeStatus,
      label: "LOW"
    };
  }
  
  // Score decreases as distance increases
  // Formula: score = 100 * (1 - distance / tolerance), clamped to 0-100
  const rawScore = 100 * (1 - distance / effectiveTolerance);
  const score = Math.max(0, Math.min(100, Math.round(rawScore * 10) / 10));
  
  // Map score to label (performance label: HIGH/MEDIUM/LOW internally)
  let label: ScoreLabel;
  if (score >= 80) {
    label = "HIGH";
  } else if (score >= 50) {
    label = "MEDIUM";
  } else {
    label = "LOW";
  }
  
  return {
    score,
    rangeStatus,
    label
  };
}

/**
 * Map performance label (HIGH/MEDIUM/LOW) to display text
 * Performance labels: "Excellent" / "Good" / "Needs improvement"
 */
export function getPerformanceLabelText(label: ScoreLabel): string {
  switch (label) {
    case "HIGH":
      return "Excellent";
    case "MEDIUM":
      return "Good";
    case "LOW":
      return "Needs improvement";
    case "PROXY":
      return "PROXY";
    case "PLACEHOLDER":
      return "PLACEHOLDER";
    default:
      return String(label);
  }
}

/**
 * Score a single metric value against its benchmark range.
 * This is the main function used by computeScoring.
 * 
 * @param value - The metric value to score
 * @param benchmark - The ideal range {low, high}
 * @param paramKey - The parameter key (for checking PROXY/PLACEHOLDER flags)
 * @returns Object with score, inRange (deprecated), rangeStatus, and label
 */
export function scoreMetric(
  value: number,
  benchmark: ParamRange,
  paramKey?: KinematicParamKey
): { 
  score: number; 
  inRange: boolean; // Deprecated: kept for backward compatibility
  rangeStatus: RangeStatus;
  label: ScoreLabel;
  reason?: string;
} {
  // Check if this metric is PROXY or PLACEHOLDER
  const def = paramKey ? METRIC_DEFS[paramKey] : undefined;
  const confidenceTag = def?.confidenceTag;
  
  // If PROXY or PLACEHOLDER, still compute rangeStatus but override label
  const rangeResult = scoreRange(value, benchmark);
  
  // Override label for PROXY/PLACEHOLDER, but keep score and rangeStatus
  let finalLabel: ScoreLabel = rangeResult.label;
  if (confidenceTag === "PROXY") {
    finalLabel = "PROXY";
  } else if (confidenceTag === "PLACEHOLDER") {
    finalLabel = "PLACEHOLDER";
    // For PLACEHOLDER, we might want to set score to 0 if value is invalid
    if (rangeResult.reason === "invalid") {
      return {
        score: 0,
        inRange: false,
        rangeStatus: rangeResult.rangeStatus,
        label: "PLACEHOLDER",
        reason: "invalid"
      };
    }
  }
  
  return {
    score: rangeResult.score,
    inRange: rangeResult.rangeStatus === "IN_RANGE", // Deprecated
    rangeStatus: rangeResult.rangeStatus,
    label: finalLabel,
    reason: rangeResult.reason
  };
}

/**
 * Compute overall scoring result for all metrics
 * @param metrics - Metric values
 * @param benchmarks - Benchmark ranges for each metric
 * @param weights - Optional weights for each metric (defaults to DEFAULT_METRIC_WEIGHTS)
 * @param apiRangeStatus - Optional API-provided rangeStatus map (source of truth if provided)
 */
export function computeScoring(
  metrics: Record<KinematicParamKey, number>,
  benchmarks: Record<KinematicParamKey, ParamRange>,
  weights: Record<KinematicParamKey, number> = DEFAULT_METRIC_WEIGHTS,
  apiRangeStatus?: Record<KinematicParamKey, RangeStatus | null>
): ScoringResult {
  const metricScores: MetricScore[] = [];
  let weightedSum = 0;
  let totalWeight = 0;
  let metricsInRange = 0;
  
  // Score each metric
  (Object.keys(metrics) as KinematicParamKey[]).forEach((paramKey) => {
    const value = metrics[paramKey];
    const benchmark = benchmarks[paramKey];
    
    if (!benchmark) {
      // Skip if no benchmark defined
      return;
    }
    
    // Use API-provided rangeStatus if available, otherwise compute client-side
    let rangeStatus: RangeStatus;
    let score: number;
    let label: ScoreLabel;
    let reason: string | undefined;
    
    if (apiRangeStatus && apiRangeStatus[paramKey] !== null && apiRangeStatus[paramKey] !== undefined) {
      // Use API-provided rangeStatus (source of truth)
      rangeStatus = apiRangeStatus[paramKey] as RangeStatus;
      // Still compute score and label client-side for display
      const scoreResult = scoreMetric(value, benchmark, paramKey);
      score = scoreResult.score;
      label = scoreResult.label;
      reason = scoreResult.reason;
      // Override rangeStatus with API value
      rangeStatus = apiRangeStatus[paramKey] as RangeStatus;
    } else {
      // Compute client-side (fallback)
      const scoreResult = scoreMetric(value, benchmark, paramKey);
      score = scoreResult.score;
      rangeStatus = scoreResult.rangeStatus;
      label = scoreResult.label;
      reason = scoreResult.reason;
    }
    
    const weight = weights[paramKey] || 0;
    
    metricScores.push({
      paramKey,
      value,
      benchmark,
      score,
      inRange: rangeStatus === "IN_RANGE", // Deprecated but kept for backward compatibility
      rangeStatus,
      label,
      reason
    });
    
    if (rangeStatus === "IN_RANGE") {
      metricsInRange++;
    }
    
    // Only include in weighted sum if not PROXY/PLACEHOLDER (or use reduced weight)
    // For now, include all metrics but PROXY/PLACEHOLDER have lower weights already
    weightedSum += score * weight;
    totalWeight += weight;
  });
  
  // Calculate overall weighted score
  const overallScore = totalWeight > 0 ? weightedSum / totalWeight : 0;
  
  // Sort by score (lowest first) to get top priorities
  const topPriorities = [...metricScores]
    .sort((a, b) => a.score - b.score)
    .slice(0, 3);
  
  // Calculate confidence summary (based on labels, not confidenceTag)
  const confidenceSummary = {
    high: 0,
    medium: 0,
    proxy: 0,
    placeholder: 0
  };
  
  metricScores.forEach((metric) => {
    if (metric.label === "HIGH") confidenceSummary.high++;
    else if (metric.label === "MEDIUM") confidenceSummary.medium++;
    else if (metric.label === "PROXY") confidenceSummary.proxy++;
    else if (metric.label === "PLACEHOLDER") confidenceSummary.placeholder++;
  });
  
  return {
    overallScore: Math.round(overallScore * 10) / 10, // Round to 1 decimal
    metricsInRange,
    totalMetrics: metricScores.length,
    metricScores,
    topPriorities,
    confidenceSummary
  };
}

/**
 * Determine level band based on overall score
 */
export function getLevelBand(score: number): 'Development' | 'Standard' | 'Elite' {
  if (score >= 80) return 'Elite';
  if (score >= 60) return 'Standard';
  return 'Development';
}
